/**
 * Where you are now. The strongest thing on the screen, and the only thing that has to be
 * readable from the piano bench without looking properly.
 *
 * ## Confidence chrome
 *
 * There are three presentations and the difference between them is the whole design:
 *
 *  - **confident** — the chord, and *nothing else*. No percentage, no meter, no tick. A
 *    confidence indicator that is always present teaches the player to distrust the app
 *    even when it is right.
 *  - **uncertain** — one quiet tappable line, "possibly E♭maj7", which is an offer to
 *    correct rather than a warning.
 *  - **ambiguous** — a dotted underline. It means *this is an interpretation*, not *this
 *    is probably wrong*. Long-press shows both hearings side by side.
 *
 * None of the three blocks anything. The player can keep playing through all of them and
 * the app never waits for an answer.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { CanonicalChord, DetectedChord, KeyEstimate, SessionNode } from '@hc/shared';
import type { ReadingQuality } from '../system/sessionPhase.js';
import { ChordSymbol } from '../system/ChordSymbol.js';
import { pitchClassNames } from '../system/chordReading.js';
import { CALM_FADE, nodeLayoutId, nodeTransition } from './motion.js';

/** How long a press has to be held before it counts as "show me both readings". */
export const LONG_PRESS_MS = 450;

export interface CurrentNodeProps {
  node: SessionNode;
  chord: CanonicalChord;
  /** The best alternative reading, already spelled. Present when uncertain or ambiguous. */
  alternative: CanonicalChord | null;
  detected: DetectedChord | null;
  reading: ReadingQuality;
  keyEstimate: KeyEstimate;
  calm: boolean;
  /** True when this node was reached by playing a suggested chord — triggers the pulse. */
  arrivedBySuggestion: boolean;
  /** Tapping the chord always opens the correction sheet, whatever the confidence. */
  onOpenCorrection: () => void;
  /**
   * Optional chrome anchored to this node — today, the ✓/✗ verdict pair when recognition
   * collection is on. A slot rather than a prop bundle because `.hc-current` is the only
   * thing on screen positioned where such chrome belongs, and because absent means the
   * node renders exactly as it always has.
   */
  chrome?: ReactNode;
}

export function CurrentNode({
  node,
  chord,
  alternative,
  detected,
  reading,
  keyEstimate,
  calm,
  arrivedBySuggestion,
  onOpenCorrection,
  chrome,
}: CurrentNodeProps): React.JSX.Element {
  const [showReadings, setShowReadings] = useState(false);
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressed = useRef(false);

  // A new chord invalidates any open comparison — it is about the previous reading.
  useEffect(() => {
    setShowReadings(false);
  }, [node.id]);

  useEffect(
    () => () => {
      if (pressTimer.current !== null) clearTimeout(pressTimer.current);
    },
    [],
  );

  const cancelPress = useCallback(() => {
    if (pressTimer.current !== null) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  }, []);

  const beginPress = useCallback(() => {
    if (reading !== 'ambiguous') return;
    longPressed.current = false;
    cancelPress();
    pressTimer.current = setTimeout(() => {
      longPressed.current = true;
      setShowReadings(true);
    }, LONG_PRESS_MS);
  }, [cancelPress, reading]);

  const endPress = useCallback(() => {
    cancelPress();
  }, [cancelPress]);

  const handleClick = useCallback(() => {
    // A long press has already done something; do not also open the sheet on release.
    if (longPressed.current) {
      longPressed.current = false;
      return;
    }
    onOpenCorrection();
  }, [onOpenCorrection]);

  const ambiguous = reading === 'ambiguous';

  return (
    <li className="hc-rail__item hc-rail__item--current">
      <motion.div
        key={node.id}
        className="hc-current"
        data-testid="hc-current-node"
        data-node-id={node.id}
        data-reading={reading}
        data-arrived={arrivedBySuggestion ? 'suggested' : 'off-path'}
        data-layout-id={calm ? undefined : nodeLayoutId(node)}
        layoutId={calm ? undefined : nodeLayoutId(node)}
        transition={nodeTransition(calm)}
      >
        <motion.div
          className="hc-current__inner"
          // Scale only, no opacity. The current chord is the one thing on screen that must
          // be readable at all times, including the frame before an animation has run.
          // `arrivedBySuggestion` overshoots slightly — that is the pulse.
          initial={calm ? false : { scale: arrivedBySuggestion ? 1.06 : 0.97 }}
          animate={{ scale: 1 }}
          transition={nodeTransition(calm)}
        >
          <button
            type="button"
            className={`hc-current__symbol${ambiguous ? ' hc-current__symbol--ambiguous' : ''}`}
            data-testid="hc-current-symbol"
            onClick={handleClick}
            onPointerDown={beginPress}
            onPointerUp={endPress}
            onPointerLeave={endPress}
            onPointerCancel={endPress}
            onContextMenu={(event) => {
              // Long-press on iOS Safari raises a context menu over the top of the panel.
              if (ambiguous) event.preventDefault();
            }}
            aria-describedby={ambiguous ? `hc-ambiguity-${node.id}` : undefined}
          >
            <ChordSymbol symbol={chord.symbol} />
          </button>

          {ambiguous ? (
            <span id={`hc-ambiguity-${node.id}`} className="hc-current__hint">
              one of two hearings — press and hold to compare
            </span>
          ) : null}

          {reading === 'uncertain' && alternative ? (
            <button
              type="button"
              className="hc-current__possibly"
              data-testid="hc-possibly"
              onClick={onOpenCorrection}
            >
              possibly <ChordSymbol symbol={alternative.symbol} />
            </button>
          ) : null}
        </motion.div>

        <AnimatePresence>
          {showReadings && alternative && detected ? (
            <motion.div
              className="hc-readings"
              data-testid="hc-readings"
              initial={{ opacity: 0, y: calm ? 0 : -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={calm ? CALM_FADE : nodeTransition(false)}
            >
              <div className="hc-readings__pane">
                <span className="hc-readings__symbol">
                  <ChordSymbol symbol={chord.symbol} />
                </span>
                <span className="hc-readings__notes">{pitchClassNames(chord, keyEstimate)}</span>
              </div>
              <span className="hc-readings__or">or</span>
              <div className="hc-readings__pane">
                <span className="hc-readings__symbol">
                  <ChordSymbol symbol={alternative.symbol} />
                </span>
                <span className="hc-readings__notes">
                  {pitchClassNames(alternative, keyEstimate)}
                </span>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        {chrome}
      </motion.div>
    </li>
  );
}
